// Google One Tap (GIS + FedCM) per la pagina di login.
// Nonce: SHA-256 hex verso Google, RAW verso Supabase signInWithIdToken
// (Supabase lo hasha e confronta — invertirli causa "Nonces mismatch").
// Doc: https://supabase.com/docs/guides/auth/social-login/auth-google
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { clear2faPendingFlag } from './useTwoFactor';
import { rememberOAuthReturnPath } from '../utils/oauthReturnPath';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

async function generateNonce(): Promise<{ raw: string; hashed: string }> {
  const raw = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return { raw, hashed };
}

function loadGisScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      // Il tag può esistere ma non aver ancora finito di caricare: aspettare il load,
      // altrimenti google.accounts.id è undefined e il hook muore in silenzio.
      if ((window as any).google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('GIS script load failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('GIS script load failed'));
    document.head.appendChild(script);
  });
}

export function useGoogleOneTap({
  enabled,
  onError,
  returnTo,
}: {
  enabled: boolean;
  onError?: (message: string) => void;
  /** Pagina richiesta prima del login: /auth/callback la riprende (vedi utils/oauthReturnPath.ts) */
  returnTo?: string | null;
}): void {
  const navigate = useNavigate();
  const initializedRef = useRef(false);
  // Callback stabile per non re-inizializzare GIS a ogni render del chiamante
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const returnToRef = useRef(returnTo);
  returnToRef.current = returnTo;

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_LOGIN_CLIENT_ID as string | undefined;
    if (!enabled || !clientId || initializedRef.current) return;

    let cancelled = false;

    (async () => {
      // Niente One Tap se esiste già una sessione
      const { data: { session } } = await supabase.auth.getSession();
      if (session || cancelled) return;

      const { raw, hashed } = await generateNonce();

      try {
        await loadGisScript();
      } catch (err) {
        console.warn('[useGoogleOneTap] GIS non caricato, One Tap disattivato:', err);
        return;
      }
      if (cancelled) return;

      const google = (window as any).google;
      if (!google?.accounts?.id) return;

      initializedRef.current = true;
      google.accounts.id.initialize({
        client_id: clientId,
        nonce: hashed,
        // FedCM è mandatory per One Tap da agosto 2025: il flag è ridondante ma esplicito.
        // NON usare i display-moment methods deprecati (isNotDisplayed, getSkippedReason...).
        use_fedcm_for_prompt: true,
        callback: async (response: { credential: string }) => {
          // Il prompt può sopravvivere all'unmount: mai agire da closure stale
          if (cancelled) return;
          // Non sovrascrivere una sessione creata nel frattempo (es. login password completato)
          const { data: { session: existing } } = await supabase.auth.getSession();
          if (existing || cancelled) return;
          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: response.credential,
            nonce: raw,
          });
          if (error) {
            console.error('[useGoogleOneTap] signInWithIdToken error:', error);
            onErrorRef.current?.('Accesso con Google non riuscito. Usa il pulsante "Continua con Google" o la password.');
            return;
          }
          // La sessione è provabilmente appena nata da Google: un eventuale flag 2FA
          // pending di un tentativo password interrotto va pulito qui (OAuthCallback
          // lo pulisce solo per sessioni con amr oauth/sso).
          clear2faPendingFlag();
          rememberOAuthReturnPath(returnToRef.current ?? null);
          // Riusa la logica post-login condivisa (org-check, registrazione, default route)
          navigate('/auth/callback', { replace: true });
        },
      });
      google.accounts.id.prompt();
    })();

    return () => {
      cancelled = true;
      // Consenti la re-inizializzazione quando enabled torna true (es. reset password chiuso)
      initializedRef.current = false;
      // Chiude il prompt One Tap se ancora visibile (unmount della pagina di login)
      (window as any).google?.accounts?.id?.cancel?.();
    };
  }, [enabled, navigate]);
}
